"use client";
import React, { useState } from "react";
import { Alert, Flex, Button } from "antd";
import Text from "antd/es/typography/Text";

import DisplayNextOpenings from "./DisplayNextOpenings";
import { SelectedService } from "../../types";
import type { DbDateString, DbTimeString, UserScheduleDTO } from "@openings/api-client";
import { formatCompactTime } from "@utils/core";
import type {
  BookingAppointmentForm,
  BookingOpeningsClient,
  BookingUser,
} from "../../types";
import type { ServiceRequestPayload } from "../../ServiceRequestForm";
import type { MediaUploadFn } from "../../ServiceRequestForm/Uploader";
import ServiceConsultation from "../../ServiceConsultation";
import {
  bookingBodyStyle,
  bookingSectionTitleStyle,
  bookingSmallStyle,
} from "../../bookingStyles";
import { TimeGrid } from "../../components/TimeGrid";

interface StylistOpeningsProps {
  salonId: string;
  user: {
    id: string;
    name: string;
    tags?: BookingUser["tags"];
  };
  teamMemberId?: string | null;
  schedules: UserScheduleDTO[];
  date: DbDateString;
  refreshCount: number;
  selectedService: SelectedService[];
  isTodayOnVacation?: boolean;
  onRefresh?: () => void;
  cancellationPolicyEnabled?: boolean;
  cancellationPolicyHours?: number | null;
  cancellationPolicyNote?: string | null;
  lateGraceMinutes?: number | null;
  verificationMethod?: "SMS" | "EMAIL";
  openingsClient: BookingOpeningsClient;
  openAppointmentForm?: (form: BookingAppointmentForm) => void;
  submitServiceRequest?: (payload: ServiceRequestPayload) => Promise<void>;
  uploadMedia?: MediaUploadFn;
  showAvailabilityStatus?: boolean;
}

export const StylistOpenings = (props: StylistOpeningsProps) => {
  const {
    salonId,
    user,
    teamMemberId,
    schedules,
    date,
    selectedService,
    refreshCount,
    isTodayOnVacation,
    cancellationPolicyEnabled,
    cancellationPolicyHours,
    cancellationPolicyNote,
    lateGraceMinutes,
    verificationMethod,
    openingsClient,
    openAppointmentForm,
    submitServiceRequest,
    uploadMedia,
    showAvailabilityStatus = false,
  } = props;
  const [isLoading, setLoading] = useState(true);
  const [resolvedQueryKey, setResolvedQueryKey] = useState<string | null>(
    null,
  );
  const [selectedOpening, setSelectedOpening] = useState<{
    scheduleId: string;
    time: DbTimeString;
  } | null>(null);
  const [openingsData, setOpeningsData] = useState<
    Array<{
      schedule: {
        id: string;
        title: string;
        address?: string | null;
        hours: UserScheduleDTO["hours"];
      };
      openings: string[];
      openingLabels: Record<string, string>;
      dayStatus?: "vacation" | "workday" | "offday";
      timezone?: string;
    }>
  >([]);

  let duration = 0;
  const servicesRequiredConsultation: SelectedService[] = [];
  selectedService.forEach((service) => {
    if (service.hasConsultation) {
      servicesRequiredConsultation.push(service);
    }
    const serviceDuration =
      service.option?.duration !== undefined
        ? service.option.duration
        : service.duration;
    duration += serviceDuration;
  });
  // Sent with each openings read so the API leaves out the starts sooner
  // than the services' minimum notice. A string, so the effect below reruns
  // only when the selection changes.
  const serviceIdsParam = selectedService.map((service) => service.id).join(",");
  const queryKey = `${date}:${refreshCount}:${duration}:${schedules
    .map((schedule) => schedule.id)
    .join(",")}:${selectedService
    .map((service) => `${service.id}:${service.option?.id ?? ""}`)
    .join("|")}`;

  React.useEffect(() => {
    setSelectedOpening(null);
  }, [queryKey]);

  const handleTimeClick = React.useCallback(
    (
      scheduleId: string,
      scheduleAddress: string,
      time: DbTimeString,
      timezone?: string,
    ) => {
      if (!openAppointmentForm) return;
      openAppointmentForm({
        salonId,
        userId: user.id,
        teamMemberId: teamMemberId ?? undefined,
        stylistName: user.name,
        memberTags: user.tags,
        scheduleAddress,
        locationName: scheduleAddress, // backward compat
        scheduleId,
        services: selectedService,
        time,
        date,
        // The zone these openings were computed in, so the confirmation's
        // calendar event lands on the business's clock (BF-DRIFT-016).
        timezone,
        cancellationPolicyEnabled,
        cancellationPolicyHours,
        cancellationPolicyNote,
        lateGraceMinutes,
        verificationMethod,
      });
    },
    [
      user.id,
      user.name,
      user.tags,
      teamMemberId,
      selectedService,
      date,
      openAppointmentForm,
      salonId,
      cancellationPolicyEnabled,
      cancellationPolicyHours,
      cancellationPolicyNote,
      lateGraceMinutes,
      verificationMethod,
    ],
  );

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setResolvedQueryKey(null);
      try {
        const promises = schedules.map(async (schedule) => {
          const scheduleId = schedule.id;
          try {
            const resp = await openingsClient.getScheduleOpenings({
              scheduleId,
              date,
              serviceDuration: duration,
              ...(serviceIdsParam
                ? { serviceIds: serviceIdsParam.split(",") }
                : {}),
            });
            if (resp.ok) {
              const data = resp.data as typeof resp.data & {
                openingLabels?: Record<string, string>;
              };
              return {
                schedule: {
                  id: scheduleId,
                  title: schedule.title,
                  address: schedule.address,
                  hours: schedule.hours,
                },
                openings: data.openings || [],
                openingLabels: data.openingLabels ?? {},
                dayStatus: data.dayStatus,
                timezone: data.timezone,
              };
            }
            return null;
          } catch (err) {
            console.error(
              `Failed to get openings for schedule ${scheduleId}:`,
              err,
            );
            return null;
          }
        });

        const results = await Promise.all(promises);
        const validResults = results.filter(
          (result): result is NonNullable<typeof result> => result !== null,
        );
        if (!cancelled) {
          setOpeningsData(validResults);
          setResolvedQueryKey(queryKey);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setOpeningsData([]);
          setResolvedQueryKey(queryKey);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (selectedService.length > 0 && duration > 0 && schedules.length > 0) {
      load();
    } else {
      setOpeningsData([]);
      setResolvedQueryKey(queryKey);
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [
    schedules,
    date,
    refreshCount,
    duration,
    selectedService.length,
    salonId,
    openingsClient,
    queryKey,
    serviceIdsParam,
  ]);

  if (servicesRequiredConsultation.length > 0) {
    if (submitServiceRequest && uploadMedia) {
      return (
        <ServiceConsultation
          salonId={salonId}
          userId={user.id}
          // Template team pages pass every schedule the member works; the
          // form makes the customer pick one so the request is never
          // unassigned. The standalone member page narrows to one first.
          scheduleOptions={schedules.map((schedule) => ({
            id: schedule.id,
            title: schedule.title,
            address: schedule.address ?? undefined,
          }))}
          services={servicesRequiredConsultation}
          submitRequest={submitServiceRequest}
          uploadMedia={uploadMedia}
        />
      );
    }
    return (
      <div>
        <p style={{ margin: 0, ...bookingSmallStyle }}>
          The selected services require a consultation before booking.
        </p>
        <Button type="primary" block>
          Send a request
        </Button>
      </div>
    );
  }

  if (schedules.length === 0) {
    return (
      <Alert
        type="info"
        message="No openings available"
        description="There are no open schedules at this moment, so we're unable to find online openings. Please contact this business directly to book."
        showIcon
      />
    );
  }

  if (isLoading || resolvedQueryKey !== queryKey)
    return (
      <Flex vertical gap="small" style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <Text style={bookingSectionTitleStyle}>Choose a time</Text>
          <span style={bookingSmallStyle}>Loading…</span>
        </div>
      </Flex>
    );

  const availableOpeningsData = openingsData.filter(
    (item) => item.openings.length > 0,
  );

  return (
    <Flex vertical gap="small" style={{ width: "100%" }}>
      {isTodayOnVacation ? (
        <div
          role="status"
          style={{
            ...bookingSmallStyle,
            padding:
              "var(--openings-booking-spacing-sm, 8px) var(--openings-booking-spacing-md, 12px)",
            border:
              "1px solid var(--openings-color-border, #e5e5e5)",
            borderRadius: "var(--openings-booking-radius-sm, 6px)",
          }}
        >
          Unavailable on this date
        </div>
      ) : (
        <>
          <Text style={bookingSectionTitleStyle}>Choose a time</Text>
          {availableOpeningsData.map((item, index) => {
            const { schedule, openings } = item;
            return (
              <Flex vertical gap="small" style={{ width: "100%" }} key={index}>
                {openingsData.length > 1 &&
                (schedule.title || schedule.address) ? (
                  <span style={bookingBodyStyle}>
                    {schedule.title || schedule.address}
                  </span>
                ) : null}
                <TimeGrid
                  times={openings as DbTimeString[]}
                  resetKey={`${queryKey}:${schedule.id}`}
                  selectionEnabled={showAvailabilityStatus}
                  selectedTime={
                    showAvailabilityStatus &&
                    selectedOpening?.scheduleId === schedule.id
                      ? selectedOpening.time
                      : null
                  }
                  onSelectedTimeChange={(time) =>
                    setSelectedOpening({ scheduleId: schedule.id, time })
                  }
                  columnCount={showAvailabilityStatus ? undefined : 3}
                  getLabel={(time) =>
                    item.openingLabels?.[time] ??
                    formatCompactTime(time, {
                      uppercase: true,
                      alwaysShowMinutes: true,
                    })
                  }
                  onSelect={(time) =>
                    handleTimeClick(
                      schedule.id,
                      schedule.address || "",
                      time,
                      item.timezone,
                    )
                  }
                />
              </Flex>
            );
          })}
        </>
      )}
      {availableOpeningsData.length === 0 && (
        <DisplayNextOpenings
          salonId={salonId}
          user={{
            id: user.id,
            name: user.name,
            tags: user.tags,
          }}
          teamMemberId={teamMemberId}
          scheduleIds={schedules.map((s) => s.id)}
          date={date}
          duration={duration}
          services={selectedService}
          onTimeClick={(args: BookingAppointmentForm) =>
            openAppointmentForm?.(args)
          }
          openingsClient={openingsClient}
          cancellationPolicyEnabled={cancellationPolicyEnabled}
          cancellationPolicyHours={cancellationPolicyHours}
          cancellationPolicyNote={cancellationPolicyNote}
          lateGraceMinutes={lateGraceMinutes}
          verificationMethod={verificationMethod}
          showAvailabilityStatus={showAvailabilityStatus}
        />
      )}
    </Flex>
  );
};

export default React.memo(StylistOpenings);
